import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import { LogIn, TriangleAlert } from 'lucide-react'

import { loginRequest } from '../config/authConfig'
import LanguageSwitcher from '../components/LanguageSwitcher'

const RETURN_URL_KEY = 'tilaajakanava_return_url'
const APP_ENV = import.meta.env.VITE_APP_ENV as string | undefined

export default function LoginPage() {
  const { t } = useTranslation()
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (isAuthenticated) {
      // Check for return URL: first from location state, then from sessionStorage
      const stateReturnUrl = (location.state as { returnUrl?: string })?.returnUrl
      const storedReturnUrl = sessionStorage.getItem(RETURN_URL_KEY)
      const returnUrl = stateReturnUrl || storedReturnUrl || '/dashboard'

      // Clear stored return URL
      sessionStorage.removeItem(RETURN_URL_KEY)

      navigate(returnUrl, { replace: true })
    }
  }, [isAuthenticated, navigate, location.state])

  const handleLogin = async () => {
    try {
      // Store return URL before redirect (MSAL redirect will lose location state)
      const stateReturnUrl = (location.state as { returnUrl?: string })?.returnUrl
      if (stateReturnUrl) {
        sessionStorage.setItem(RETURN_URL_KEY, stateReturnUrl)
      }

      await instance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 to-primary-100">
      <header className="flex justify-end p-4">
        <LanguageSwitcher />
      </header>
      
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {(APP_ENV === 'staging' || APP_ENV === 'development') && (
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-4 text-sm font-medium ${
              APP_ENV === 'staging'
                ? 'bg-orange-100 border border-orange-300 text-orange-900'
                : 'bg-yellow-100 border border-yellow-300 text-yellow-900'
            }`}>
              <TriangleAlert className="w-5 h-5 flex-shrink-0" />
              <span>
                {APP_ENV === 'staging'
                  ? t('envBanner.staging')
                  : t('envBanner.development')}
              </span>
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <img 
                src="/images/logo-th.webp" 
                alt="Taksi Helsinki" 
                className="h-16 mx-auto mb-4"
              />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t('appName')}
              </h1>
              <p className="text-gray-600">
                {t('auth.welcome')}
              </p>
            </div>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              <LogIn className="w-5 h-5 mr-2" />
              {t('auth.loginWithMicrosoft')}
            </button>
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </main>
    </div>
  )
}
